import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import useCreateLectureItem from "../hooks/useCreateLectureItem";

import Input from "@/components/input/Input";
import Button from "@/components/Button";
import Select from "@/components/select/Select";
import { Spinner } from "@nextui-org/react";
import { convertToEmbedLink } from "../../../../utils/helpers";

function CreateLectureItem({ onCloseModal }) {
    const { courseId, lectureId } = useParams();
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        watch,
    } = useForm();

    const {
        createLectureItem,
        isCreating,
        error: ApiError,
    } = useCreateLectureItem();

    function onSubmit(data) {
        let payload;

        if (data.type === "video") {
            const iframeFormat = convertToEmbedLink(data.iframe);

            payload = {
                name: data.name,
                brief: data.brief,
                iframe: iframeFormat,
                type: data.type,
                lectureId,
                courseId,
                video_views: data.video_views,
            };
        } else if (data.type === "quiz") {
            payload = {
                name: data.name,
                brief: data.brief,
                type: data.type,
                quiz_duration: data.quiz_duration,
                quiz_success_percentage: data.quiz_success_percentage,
                quiz_attempt_limit: data.quiz_attempt_limit,
                lectureId,
                courseId,
            };
        }

        createLectureItem(
            {
                ...payload,
                courseId,
                lectureId,
            },
            {
                onSuccess: () => {
                    reset();
                    onCloseModal?.();
                },
            }
        );
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="pt-6 space-y-4">
            {/* Name */}
            <Input
                label={"العنوان"}
                name={"name"}
                type="text"
                register={register("name", {
                    maxLength: {
                        value: 30,
                        message: "الوصف الكورس يجب ان لا يزيد عن 30 حرف",
                    },
                    required: "برجاء إدخال العنوان ",
                })}
                error={
                    errors?.name?.message ||
                    ApiError?.response?.data?.errors?.name?.[0]
                }
                disabled={isCreating}
            />

            {/* Brief */}
            <Input
                label={"الوصف"}
                name={"brief"}
                type="text"
                register={register("brief", {
                    maxLength: {
                        value: 50,
                        message: "الوصف الكورس يجب ان لا يزيد عن 50 حرف",
                    },
                    required: "برجاء إدخال الوصف",
                })}
                error={
                    errors?.brief?.message ||
                    ApiError?.response?.data?.errors?.brief?.[0]
                }
                disabled={isCreating}
            />

            {/* Type */}
            <Select
                name="type"
                label="نوع المحتوي*"
                disabled={isCreating}
                setValue={setValue}
                watch={watch}
                register={register("type", {
                    required: "برجاء إدخال نوع المحتوي",
                })}
                error={
                    errors?.type?.message ||
                    ApiError?.response?.data?.errors?.type?.[0]
                }
                options={[
                    { value: "video", text: "فيديو" },
                    { value: "quiz", text: "إختبار" },
                ]}
            />
            {watch().type === "video" && (
                <>
                    <Input
                        label={"لينك الفيديو"}
                        name={"iframe"}
                        type="text"
                        register={register("iframe", {
                            required: "برجاء إدخال لينك الفيديو",
                        })}
                        error={
                            errors?.iframe?.message ||
                            ApiError?.response?.data?.errors?.iframe?.[0]
                        }
                        disabled={isCreating}
                    />
                    <Input
                        label={"عدد المشاهدات"}
                        name={"video_views"}
                        type="number"
                        register={register("video_views", {
                            required: "برجاء إدخال عدد مشاهدات الفيديو",
                            min: {
                                value: 0,
                                message:
                                    "عدد مشاهدات الفيديو يجب أن يكون على الأقل 0",
                            },
                        })}
                        error={
                            errors?.video_views?.message ||
                            ApiError?.response?.data?.errors?.video_views?.[0]
                        }
                        disabled={isCreating}
                    />
                </>
            )}
            {/* Quiz */}
            {watch().type === "quiz" && (
                <>
                    {/* Quiz Percentage */}
                    <Input
                        label={"نسبة النجاح"}
                        name={"quiz_success_percentage"}
                        type="text"
                        register={register("quiz_success_percentage", {
                            required: "برجاء إدخال نسبة النجاح",
                        })}
                        error={
                            errors?.quiz_success_percentage?.message ||
                            ApiError?.response?.data?.errors
                                ?.quiz_success_percentage?.[0]
                        }
                        disabled={isCreating}
                    />

                    {/* Quiz Time */}
                    <Input
                        label={" وقت الأمتحان بالدقائق"}
                        name={"quiz_duration"}
                        type="text"
                        register={register("quiz_duration", {
                            required: "برجاء إدخال وقت الأمتحان",
                        })}
                        error={
                            errors?.quiz_duration?.message ||
                            ApiError?.response?.data?.errors?.quiz_duration?.[0]
                        }
                        disabled={isCreating}
                    />
                    <Select
                        name="quizAttempt"
                        label="عدد مرات دخول الأمتحان*"
                        disabled={isCreating}
                        setValue={setValue}
                        watch={watch}
                        register={register("quizAttempt", {
                            required: "برجاء إدخال عدد مرات دخول الأمتحان",
                        })}
                        error={errors?.quizAttempt?.message}
                        options={[
                            { value: "limited", text: "محدوده" },
                            { value: "unlimited", text: "غير محدوده" },
                        ]}
                    />

                    {/* Quiz Attempt Limit */}
                    {watch().quizAttempt === "limited" && (
                        <Input
                            label={" عدد مرات دخول الامتحان"}
                            name={"quiz_attempt_limit"}
                            register={register("quiz_attempt_limit")}
                            error={
                                errors?.quiz_attempt_limit?.message ||
                                ApiError?.response?.data?.errors
                                    ?.quiz_attempt_limit?.[0]
                            }
                            type="text"
                            disabled={isCreating}
                        />
                    )}
                </>
            )}
            <div className="w-full pt-10">
                <Button
                    type="primary"
                    className="flex items-center justify-center w-full leading-8"
                    disabled={isCreating}
                >
                    {isCreating ? (
                        <Spinner color="white" />
                    ) : (
                        "إضافة محتوي للمحاضره"
                    )}
                </Button>
            </div>
        </form>
    );
}

export default CreateLectureItem;
